import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { REVIEWS } from "@/data";
import { Reveal, SectionHeading } from "@/components/Reveal";

const FILTERS = ["All", "Digital Marketing", "Web Development", "Affiliate Marketing"];

export default function Testimonials() {
  const [filter, setFilter] = useState("All");
  const [index, setIndex] = useState(0);

  const filtered = useMemo(
    () => (filter === "All" ? REVIEWS : REVIEWS.filter((r) => r.category === filter)),
    [filter]
  );
  const current = filtered[index % filtered.length];

  const pick = (f) => {
    setFilter(f);
    setIndex(0);
  };

  return (
    <section id="reviews" data-testid="testimonials-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-0 left-1/3 w-[26rem] h-[26rem] rounded-full bg-[#D9A441]/10 blur-[130px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading id="reviews" overline="Reviews / 05" title="Words from clients" />

        <Reveal className="mb-10 flex flex-wrap gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => pick(f)}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs font-mono uppercase tracking-widest rounded-full px-4 py-2 border transition-all duration-300 ${
                filter === f
                  ? "border-[#D9A441]/60 text-[#D9A441] bg-[#D9A441]/10"
                  : "border-white/15 text-[#9DAEC4] hover:text-[#F3EFE6] hover:border-white/30"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>

        <div data-testid="carousel-testimonials" className="relative max-w-4xl">
          <AnimatePresence mode="wait">
            <motion.figure
              key={`${filter}-${index % filtered.length}`}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl border border-white/10 bg-[#101F38] shadow-xl shadow-black/30 p-8 sm:p-12"
              data-testid="testimonial-card"
            >
              <Quote size={34} className="text-[#D9A441]/50 mb-6" />
              <blockquote className="text-lg sm:text-xl text-[#EDEAE0] leading-relaxed font-editorial italic">
                "{current.text}"
              </blockquote>
              <figcaption className="mt-8 flex flex-wrap items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 rounded-full bg-[#D9A441]/15 border border-[#D9A441]/30 flex items-center justify-center font-display font-bold text-[#D9A441]">
                    {current.name.split(" ").map((w) => w[0]).join("")}
                  </span>
                  <div>
                    <p className="font-semibold text-[#F3EFE6]">{current.name}</p>
                    <p className="text-sm text-[#9DAEC4]">
                      {current.role} · {current.location}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex gap-1 justify-end" data-testid="testimonial-rating">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={15} className={i < current.rating ? "fill-[#D9A441] text-[#D9A441]" : "text-white/15"} />
                    ))}
                  </div>
                  <p className="mt-2 text-xs font-mono uppercase tracking-widest text-emerald-400">{current.result}</p>
                </div>
              </figcaption>
            </motion.figure>
          </AnimatePresence>

          <div className="mt-8 flex items-center gap-4">
            <button
              onClick={() => setIndex((i) => (i - 1 + filtered.length) % filtered.length)}
              data-testid="btn-testimonial-prev"
              className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-[#C8D3E0] hover:border-[#D9A441]/60 hover:text-[#D9A441] transition-all duration-300"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => setIndex((i) => (i + 1) % filtered.length)}
              data-testid="btn-testimonial-next"
              className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-[#C8D3E0] hover:border-[#D9A441]/60 hover:text-[#D9A441] transition-all duration-300"
            >
              <ChevronRight size={18} />
            </button>
            <span className="font-mono text-xs text-[#9DAEC4] tracking-widest" data-testid="testimonial-counter">
              {String((index % filtered.length) + 1).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
